/**
 * A tiny single-table pinball engine. Canvas 2D, fixed-timestep physics
 * with sub-stepping so the ball doesn't tunnel thin walls.
 *
 * createPinball(canvas, { onScore, onBalls, onGameOver, onCatch, onBump,
 *   onDrain, pickMon })
 *   → { start, stop, newGame, setFlipper('L'|'R', down), setPlunge(down) }
 *
 * All geometry is in logical units (a W×H table); the renderer scales to
 * the canvas backing store. Tune feel via TABLE / PHYS below.
 */

const W = 320;
const H = 560;

const PHYS = {
  gravity: 1220, // units/s² — heavier than a floaty ball, eased back a touch from 1320
  drag: 0.9997, // per sub-step (×substeps×fps) — keep close to 1 or the ball dies fast
  maxSpeed: 1300, // must clear the launch's own top speed (1240) or the plunger's power curve gets clipped flat
  substeps: 5,
  ballR: 7,
  wallE: 0.34, // wall restitution
  flipE: 0.28,
  flipThick: 6,
  flipLen: 50,
  flipSwing: 1.02, // radians
  flipSpeed: 34, // ease rate toward target angle
  bumperE: 0.55,
  bumperKick: 116,
  postE: 0.75,
  postKick: 70,
  slingE: 1.3,
  slingKick: 230, // a real kick away from the rubber, not a nudge
  spin: 0.07, // Poké Ball rotation per unit travelled
};

/** Points along an arc a0→a1 (radians, y-down), inclusive of both ends. */
function arcPts(cx, cy, r, a0, a1, n) {
  const p = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    p.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  return p;
}
/** Consecutive points → wall segments sharing `extra` (role, e, k…). */
function polySegs(pts, extra) {
  const s = [];
  for (let i = 0; i < pts.length - 1; i++) s.push({ a: pts[i], b: pts[i + 1], ...extra });
  return s;
}

const PI = Math.PI;
const D = PI / 180;

// The playfield is a rounded box. The usable area runs x≈16..264; the
// shooter lane is the strip to its right (x≈264..304). Everything below
// is built symmetrically about the centre line so nothing looks stitched.
const CX = 160; // centre of the whole table, tunnel included (canvas centre)

// Outer table outline — a rounded rectangle that fills the canvas edge to
// edge (just a hairline corner radius), so there's no dark "apron" framing
// the tan floor — it's the whole visible table.
const OUTLINE = [
  ...arcPts(18, 18, 16, 180 * D, 270 * D, 6), // TL
  ...arcPts(302, 18, 16, 270 * D, 360 * D, 6), // TR
  ...arcPts(302, 542, 16, 0, 90 * D, 6), // BR
  ...arcPts(18, 542, 16, 90 * D, 180 * D, 6), // BL
];

// This table's layout was hand-tuned in the #/pinball-editor dev tool
// (drag-to-edit, then exported as JSON) rather than computed — that's why
// the numbers below don't fall into neat patterns. See PinballEditorView.js.
//
// Collision boundary: left wall + rounded top + right wall, sweeping in
// at the bottom on both sides — that's the true outlane (a ball that
// stays outboard of the OUTLANE_* guides below rides this wall straight
// past the flipper to the drain).
const BOUND = [
  [70, 550],
  [22, 520],
  [8, 446],
  [8, 50], // wall pushed out to the edge of the (now edge-to-edge) floor
  ...arcPts(48, 50, 40, 180 * D, 270 * D, 8), // TL corner → (48,10)
  [261, 10],
  [270, 11],
  [278, 13],
  [285, 17],
  [292, 23],
  [298, 30],
  [303, 37],
  [304, 45],
  [305, 54],
  [305, 451],
  [307, 535],
  [267, 552],
];

// Outlane tubes — a second wall inboard of each side. A ball that rides
// this one (instead of drifting further out to BOUND) is fed onto the
// flipper — it's effectively the inlane guide, ending right at the pivot.
const OUTLANE_L = [
  [54, 405],
  [54, 445],
  [57, 500],
  [102, 522],
];
const OUTLANE_R = [
  [266, 405],
  [266, 445],
  [263, 500],
  [218, 522],
];

const LANE_OUTER = [
  [306, 505],
  [304, 139],
];
const LANE_FLOOR = [
  [306, 504],
  [288, 504],
];
const LANE_INNER = [
  [288, 506],
  [288, 151],
  [287, 145],
  [285, 139],
  [281, 134],
  [276, 130],
  [271, 127],
];

// The shipped table. createPinball() takes this as its default, but a
// caller (the #/pinball-editor dev tool, notably) can pass a differently
// shaped table of the same form to test a layout before it's committed.
const DEFAULT_TABLE = {
  outline: OUTLINE,
  bound: BOUND,
  outlaneL: OUTLANE_L,
  outlaneR: OUTLANE_R,
  laneOuter: LANE_OUTER,
  laneFloor: LANE_FLOOR,
  laneInner: LANE_INNER,
  walls: [
    ...polySegs(BOUND, { role: 'wall' }),
    ...polySegs(OUTLANE_L, { role: 'guide' }),
    ...polySegs(OUTLANE_R, { role: 'guide' }),

    // shooter lane — a narrow, separate channel to the right of the
    // playfield. Its inner wall runs up to hold the resting ball, then
    // curls left into a hood that steers a launched ball into the
    // playfield ("hood delivery" in step() does the heavy lifting).
    ...polySegs(LANE_OUTER, { role: 'lane' }),
    ...polySegs(LANE_FLOOR, { role: 'lane' }),
    ...polySegs(LANE_INNER, { role: 'lane' }),

    // slingshots — tight above each flipper's outer half
    { a: [71, 422], b: [139, 457], e: PHYS.slingE, k: PHYS.slingKick, role: 'sling' },
    { a: [71, 422], b: [96, 456], role: 'sling' },
    { a: [96, 456], b: [139, 457], role: 'sling' },
    { a: [249, 422], b: [181, 457], e: PHYS.slingE, k: PHYS.slingKick, role: 'sling' },
    { a: [249, 422], b: [224, 456], role: 'sling' },
    { a: [224, 456], b: [181, 457], role: 'sling' },
  ],
  bumpers: [
    { x: 114, y: 114, r: 15, kind: 'poke' },
    { x: 160, y: 72, r: 16, kind: 'poke' },
    { x: 206, y: 114, r: 15, kind: 'poke' },
    { x: 136, y: 182, r: 12, kind: 'great' },
    { x: 184, y: 182, r: 12, kind: 'great' },
  ],
  posts: [
    { x: 64, y: 283, r: 7 },
    { x: 256, y: 283, r: 7 },
    { x: 160, y: 279, r: 8 },
  ],
  flippers: {
    // Same size on both sides, mirrored pivots — centred on the whole
    // table (playfield + shooter lane), not just the playfield alone.
    L: { pivot: [96, 524], rest: 0.28, len: 56 }, // radians, y-down; tip points down-right
    R: { pivot: [224, 524], rest: Math.PI - 0.28, len: 56 },
  },
  laneX: 295, // ball rest x in the shooter lane
  laneRestY: 488,
  drainY: 546,
  returnMinX: 999, // no return chute — every side feeds a flipper or an outlane
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Closest point on segment a→b to p, plus the parametric t. */
function closestOnSeg(px, py, ax, ay, bx, by) {
  const abx = bx - ax;
  const aby = by - ay;
  const denom = abx * abx + aby * aby || 1;
  const t = clamp(((px - ax) * abx + (py - ay) * aby) / denom, 0, 1);
  return [ax + t * abx, ay + t * aby, t];
}

export const _test = { closestOnSeg, clamp, W, H, TABLE: DEFAULT_TABLE, PHYS };

/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} [cbs] — onScore, onBalls, onGameOver, onCatch, onBump,
 *   onDrain, onCharge, pickMon
 * @param {object} [tableOverride] — a table of the same shape as the
 *   built-in one (see DEFAULT_TABLE), for the #/pinball-editor "play test"
 *   mode. Omit to play the shipped table.
 */
export function createPinball(canvas, cbs = {}, tableOverride) {
  const TABLE = tableOverride || DEFAULT_TABLE;
  const ctx = canvas.getContext('2d');
  const accent =
    getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#b3161a';

  const state = {
    running: false,
    raf: 0,
    last: 0,
    score: 0,
    balls: 3,
    over: false,
    ball: null, // { x, y, vx, vy, inLane }
    flip: { L: false, R: false },
    ang: { L: TABLE.flippers.L.rest, R: TABLE.flippers.R.rest },
    angPrev: { L: TABLE.flippers.L.rest, R: TABLE.flippers.R.rest },
    omega: { L: 0, R: 0 },
    plunge: false,
    chargeStart: 0, // performance.now() when the plunger was pressed
    charge: 0, // 0..1, for the plunger visual only
    lit: TABLE.bumpers.map(() => false),
    catchT: 0,
    catchMon: null,
    scale: 1,
  };

  function spawnBall(toLane = true) {
    state.ball = {
      x: TABLE.laneX,
      y: TABLE.laneRestY - 4,
      vx: 0,
      vy: 0,
      spin: 0,
      inLane: toLane,
    };
    state.charge = 0;
    state.chargeStart = 0;
  }

  const PULL_MS = 900; // hold this long for a full-strength launch

  function newGame() {
    state.score = 0;
    state.balls = 3;
    state.over = false;
    state.lit = TABLE.bumpers.map(() => false);
    spawnBall();
    cbs.onScore?.(0);
    cbs.onBalls?.(3);
  }

  function addScore(n) {
    state.score += n;
    cbs.onScore?.(state.score);
  }

  function flipperSeg(which) {
    const f = TABLE.flippers[which];
    const ang = state.ang[which];
    const len = f.len ?? PHYS.flipLen;
    return {
      ax: f.pivot[0],
      ay: f.pivot[1],
      bx: f.pivot[0] + Math.cos(ang) * len,
      by: f.pivot[1] + Math.sin(ang) * len,
    };
  }

  function collideSeg(b, ax, ay, bx, by, e, half, svx = 0, svy = 0) {
    const [cx, cy] = closestOnSeg(b.x, b.y, ax, ay, bx, by);
    let dx = b.x - cx;
    let dy = b.y - cy;
    let d = Math.hypot(dx, dy);
    const minD = PHYS.ballR + half;
    if (d >= minD) return false;
    if (d < 0.0001) {
      dx = 0;
      dy = -1;
      d = 1;
    }
    const nx = dx / d;
    const ny = dy / d;
    const rvx = b.vx - svx;
    const rvy = b.vy - svy;
    const vn = rvx * nx + rvy * ny;
    if (vn < 0) {
      const j = -(1 + e) * vn;
      b.vx += j * nx;
      b.vy += j * ny;
    }
    b.x += nx * (minD - d);
    b.y += ny * (minD - d);
    return true;
  }

  function step(dt) {
    const b = state.ball;
    if (!b || state.over) return;

    // Flippers ease toward their target angle; ω drives the ball on contact.
    for (const which of ['L', 'R']) {
      const f = TABLE.flippers[which];
      const dir = which === 'L' ? -1 : 1;
      const target = state.flip[which] ? f.rest + dir * PHYS.flipSwing : f.rest;
      state.angPrev[which] = state.ang[which];
      state.ang[which] += (target - state.ang[which]) * clamp(PHYS.flipSpeed * dt, 0, 1);
      state.omega[which] = (state.ang[which] - state.angPrev[which]) / dt;
    }

    // Plunger — the ball tracks the lane centre until it's launched.
    if (b.inLane && b.x > 291) {
      b.vx *= 0.6;
      b.x += (TABLE.laneX - b.x) * 0.35;
    }
    // If a launch fizzled (never reached the gate) and the ball is dropping
    // back down the lane, forget the launch so the plunger can re-arm.
    if (b.launching && b.vy > 0 && b.y > 320) b.launching = false;
    // Re-arm the plunger for any near-stationary ball back in the lane.
    if (!b.inLane && !b.launching && b.x > 289 && b.y > 460 && Math.hypot(b.vx, b.vy) < 140) {
      b.inLane = true;
      b.x = TABLE.laneX;
      b.vx = 0;
    }
    // Hood delivery: the shooter lane and the playfield's top-right corner
    // share a cramped space where wall bounces reliably ping a launched
    // ball straight back down the tube. So once the ball nears the top of
    // the lane we fly it into the playfield on a scripted arc — collisions
    // off (bumpers/posts/slings stay solid) — until it's clearly among the
    // bumpers. The charge sets how hard it cuts across.
    if (b.launching && b.y < 145) {
      b.vx = -(300 + 250 * state.launchC);
      b.vy = 40;
      b.launching = false;
    }
    // End the scripted glide once the kick has fired (b.launching cleared)
    // and the ball is well left of the shooter lane — or, as a safety, has
    // dropped past the bumpers.
    if (b.hooding && !b.launching && (b.x < 220 || b.y > 260)) b.hooding = false;

    b.vy += PHYS.gravity * dt;
    b.x += b.vx * dt;
    b.y += b.vy * dt;

    if (!b.hooding)
      for (const w of TABLE.walls) {
        if (collideSeg(b, w.a[0], w.a[1], w.b[0], w.b[1], w.e ?? PHYS.wallE, 0) && w.k) {
          // slingshot face: shove the ball away from the segment + score
          const mx = (w.a[0] + w.b[0]) / 2;
          const my = (w.a[1] + w.b[1]) / 2;
          const nx = b.x - mx;
          const ny = b.y - my;
          const nl = Math.hypot(nx, ny) || 1;
          b.vx += (nx / nl) * w.k;
          b.vy += (ny / nl) * w.k;
          addScore(50);
        }
      }
    if (b.hooding) {
      // still gliding out of the hood — skip the rest of the collisions
      b.vx *= PHYS.drag;
      b.vy *= PHYS.drag;
      return;
    }
    for (const p of TABLE.posts) {
      const dx = b.x - p.x;
      const dy = b.y - p.y;
      const d = Math.hypot(dx, dy) || 1;
      if (d < PHYS.ballR + p.r) {
        const nx = dx / d;
        const ny = dy / d;
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0) {
          const j = -(1 + PHYS.postE) * vn;
          b.vx += j * nx;
          b.vy += j * ny;
        }
        b.vx += nx * PHYS.postKick;
        b.vy += ny * PHYS.postKick;
        b.x = p.x + nx * (PHYS.ballR + p.r + 0.5);
        b.y = p.y + ny * (PHYS.ballR + p.r + 0.5);
        addScore(25);
        cbs.onBump?.('post');
      }
    }
    for (let i = 0; i < TABLE.bumpers.length; i++) {
      const bp = TABLE.bumpers[i];
      let dx = b.x - bp.x;
      let dy = b.y - bp.y;
      const d = Math.hypot(dx, dy);
      if (d < PHYS.ballR + bp.r) {
        if (d < 0.001) {
          dx = 0;
          dy = -1;
        }
        const nx = dx / (d || 1);
        const ny = dy / (d || 1);
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0) {
          const j = -(1 + PHYS.bumperE) * vn;
          b.vx += j * nx;
          b.vy += j * ny;
        }
        b.vx += nx * PHYS.bumperKick;
        b.vy += ny * PHYS.bumperKick;
        b.x = bp.x + nx * (PHYS.ballR + bp.r + 0.5);
        b.y = bp.y + ny * (PHYS.ballR + bp.r + 0.5);
        addScore(100);
        cbs.onBump?.('bumper');
        if (!state.lit[i]) {
          state.lit[i] = true;
          if (state.lit.every(Boolean)) {
            state.lit = TABLE.bumpers.map(() => false);
            addScore(5000);
            const mon = cbs.pickMon?.() || { id: 0, name: 'Something' };
            state.catchMon = mon;
            state.catchT = 2.2;
            cbs.onCatch?.(mon);
          }
        }
      }
    }
    for (const which of ['L', 'R']) {
      const s = flipperSeg(which);
      const [cx, cy] = closestOnSeg(b.x, b.y, s.ax, s.ay, s.bx, s.by);
      const svx = -state.omega[which] * (cy - s.ay);
      const svy = state.omega[which] * (cx - s.ax);
      collideSeg(b, s.ax, s.ay, s.bx, s.by, PHYS.flipE, PHYS.flipThick, svx, svy);
    }

    b.vx *= PHYS.drag;
    b.vy *= PHYS.drag;
    const sp = Math.hypot(b.vx, b.vy);
    if (sp > PHYS.maxSpeed) {
      b.vx *= PHYS.maxSpeed / sp;
      b.vy *= PHYS.maxSpeed / sp;
    }
    b.spin += b.vx * dt * PHYS.spin; // Poké Ball rolls with its horizontal motion

    // Ball-search: if the ball hasn't travelled 22 units in 1.1s (and isn't
    // waiting in the shooter lane) it's wedged — kick it toward centre, like
    // a real table. Four strikes and it drains.
    b.trackT = (b.trackT || 0) + dt;
    if (b.trackX == null) {
      b.trackX = b.x;
      b.trackY = b.y;
    }
    if (b.trackT > 1.1) {
      const moved = Math.hypot(b.x - b.trackX, b.y - b.trackY);
      if (!b.inLane && !b.hooding && moved < 22) {
        if ((b.searches || 0) >= 3) {
          loseBall();
          return;
        }
        b.searches = (b.searches || 0) + 1;
        const kx = CX - b.x;
        const ky = 200 - b.y;
        const kl = Math.hypot(kx, ky) || 1;
        b.vx = (kx / kl) * 430;
        b.vy = (ky / kl) * 430;
      } else {
        b.searches = 0;
      }
      b.trackT = 0;
      b.trackX = b.x;
      b.trackY = b.y;
    }

    if (b.y > TABLE.drainY) {
      if (b.x >= TABLE.returnMinX)
        spawnBall(true); // right outlane — back to the plunger, no life lost
      else loseBall();
    }
  }

  function loseBall() {
    state.balls -= 1;
    cbs.onBalls?.(state.balls);
    cbs.onDrain?.();
    if (state.balls <= 0) {
      state.over = true;
      state.ball = null;
      cbs.onGameOver?.(state.score);
    } else {
      spawnBall();
    }
  }

  // --- render ---------------------------------------------------------
  function resize() {
    // Measure from the outer view, not the stage: the stage now hugs the
    // canvas (width: fit-content, so its own background doesn't show as
    // margins), which would make measuring it circular — it'd just report
    // back whatever size the canvas already is.
    const host = canvas.parentElement?.parentElement || canvas.parentElement;
    let cw = Math.max(200, Math.min(host ? host.clientWidth : 320, 520));
    let ch = cw * (H / W);
    // Keep the whole table on screen without scrolling: cap the height to
    // what's left of the viewport after the nav, HUD, and controls.
    const availH = (window.innerHeight || 800) - 250;
    if (ch > availH) {
      ch = Math.max(340, availH);
      cw = ch * (W / H);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    state.scale = canvas.width / W;
    render();
  }

  /** A Poké Ball centred at (cx,cy), radius r, rotated by ang. */
  function pokeball(cx, cy, r, ang, topColor) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(ang);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#f4f4f6';
    ctx.fillRect(-r, -r, r * 2, r * 2);
    ctx.fillStyle = topColor;
    ctx.fillRect(-r, -r, r * 2, r);
    const band = Math.max(2.4, r * 0.3);
    ctx.fillStyle = '#17181d';
    ctx.fillRect(-r, -band / 2, r * 2, band);
    const br = Math.max(2, r * 0.36);
    ctx.beginPath();
    ctx.arc(0, 0, br, 0, Math.PI * 2);
    ctx.fillStyle = '#17181d';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 0, br * 0.55, 0, Math.PI * 2);
    ctx.fillStyle = '#f4f4f6';
    ctx.fill();
    ctx.restore();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(1, r * 0.14);
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.stroke();
  }

  const accentRGB = (() => {
    const h = /^#([0-9a-f]{6})$/i.test(accent) ? accent : '#b3161a';
    return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  })();
  const accentA = (a) => `rgba(${accentRGB[0]},${accentRGB[1]},${accentRGB[2]},${a})`;

  // Flat, bright "Pokémon stadium" palette — solid fills, crisp dark
  // outlines, no glow. A battle-arena floor with a Poké Ball painted on
  // it, ringed by stadium seating.
  const C = {
    arena: '#d8b878', // arena floor (sandy battle ground)
    arenaAlt: '#cdaa66', // inner ring of the floor
    apron: '#2b3040', // outside the arena bowl
    seatA: '#454c63', // stadium seating bands
    seatB: '#3a4056',
    court: 'rgba(255,255,255,0.34)', // Poké Ball court art
    courtRed: 'rgba(196,32,32,0.16)',
    barrier: '#eef0f2', // stadium barrier fill
    edge: '#1c2230', // universal dark outline
    rubber: '#e5473a', // slingshots + rubber deflectors
    rubberTop: '#ff6a5c',
    flip: '#f7c948', // flippers (Pikachu yellow)
    flipTop: '#ffe08a',
    post: '#3d6bd6', // mid-field pegs (blue)
    postTop: '#6f97ee',
    metal: '#b8bdc7', // plunger spring
  };

  /** Stroke the current path: dark outline, then a coloured core on top. */
  function stroke2(core, coreW, edgeW = coreW + 3) {
    ctx.lineWidth = edgeW;
    ctx.strokeStyle = C.edge;
    ctx.stroke();
    ctx.lineWidth = coreW;
    ctx.strokeStyle = core;
    ctx.stroke();
  }
  /** Filled circle with a dark outline and a lighter top cap (a "puck"). */
  function puck(x, y, r, fill, top) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (top) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.beginPath();
      ctx.arc(x, y - r * 0.32, r * 0.82, 0, Math.PI * 2);
      ctx.fillStyle = top;
      ctx.fill();
      ctx.restore();
    }
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = C.edge;
    ctx.stroke();
  }
  const tri = (pts) => {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    ctx.lineTo(pts[1][0], pts[1][1]);
    ctx.lineTo(pts[2][0], pts[2][1]);
    ctx.closePath();
  };

  /** Trace a polyline as a smooth curve (quadratics through midpoints). */
  function tracePath(pts, close) {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2;
      const my = (pts[i][1] + pts[i + 1][1]) / 2;
      ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last[0], last[1]);
    if (close) ctx.closePath();
  }

  /** A tapered flipper bat: wide at the pivot, rounded at the tip. */
  function bat(ax, ay, bx, by, wr, wt, fill) {
    const ang = Math.atan2(by - ay, bx - ax);
    const px = -Math.sin(ang);
    const py = Math.cos(ang);
    ctx.beginPath();
    ctx.moveTo(ax + px * wr, ay + py * wr);
    ctx.lineTo(bx + px * wt, by + py * wt);
    ctx.lineTo(bx - px * wt, by - py * wt);
    ctx.lineTo(ax - px * wr, ay - py * wr);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ax, ay, wr, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(bx, by, wt, 0, Math.PI * 2);
    ctx.fill();
  }

  /** A pop bumper sitting on the table — skirt, cap, gloss. */
  function bumper(x, y, r, kind, lit) {
    ctx.beginPath();
    ctx.arc(x, y, r + 5, 0, Math.PI * 2);
    ctx.fillStyle = C.edge;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, r + 5, 0, Math.PI * 2);
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = lit ? '#ffd23f' : 'rgba(255,255,255,0.45)';
    ctx.stroke();
    pokeball(x, y, r, 0, lit ? '#ffd23f' : kind === 'great' ? '#3d7fd6' : accent);
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.beginPath();
    ctx.arc(x - r * 0.3, y - r * 0.42, r * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fill();
    ctx.restore();
  }

  function render() {
    ctx.setTransform(state.scale, 0, 0, state.scale, 0, 0);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // outside the bowl
    ctx.fillStyle = C.apron;
    ctx.fillRect(0, 0, W, H);

    // stadium seating — a thin crowd sliver above the (now taller) field
    for (let row = 0; row < 2; row++) {
      const yy = 6 + row * 7;
      ctx.fillStyle = row % 2 ? C.seatA : C.seatB;
      ctx.fillRect(0, yy - 3, W, 6);
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      for (let xx = 5; xx < W; xx += 9) ctx.fillRect(xx, yy - 2, 3, 5);
    }

    // arena floor — the rounded table outline, filling the stage
    tracePath(TABLE.outline, true);
    ctx.fillStyle = C.arena;
    ctx.fill();
    stroke2(C.edge, 2, 2);

    // Poké Ball painted on the floor, centred on the usable playfield
    ctx.save();
    tracePath(TABLE.outline, true);
    ctx.clip();
    ctx.beginPath();
    ctx.arc(CX, 262, 128, Math.PI, 0);
    ctx.fillStyle = C.courtRed;
    ctx.fill();
    ctx.strokeStyle = C.court;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(CX, 262, 128, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(CX - 128, 262);
    ctx.lineTo(CX + 128, 262);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(CX, 262, 34, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    // slingshot wedges — solid rubber, lighter top face
    for (const pts of [
      [
        [71, 422],
        [139, 457],
        [96, 456],
      ],
      [
        [249, 422],
        [181, 457],
        [224, 456],
      ],
    ]) {
      tri(pts);
      ctx.fillStyle = C.rubber;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = C.edge;
      ctx.stroke();
      tri([pts[0], pts[1], [(pts[1][0] + pts[2][0]) / 2, (pts[1][1] + pts[2][1]) / 2]]);
      ctx.fillStyle = C.rubberTop;
      ctx.fill();
    }

    // lane + outlane guides — smooth barriers, not a chain of straight
    // segments (each is its own connected line, unlike the flat wall list)
    for (const pts of [TABLE.outlaneL, TABLE.outlaneR, TABLE.laneOuter, TABLE.laneInner]) {
      tracePath(pts, false);
      stroke2(C.barrier, 3, 6);
    }
    tracePath(TABLE.laneFloor, false);
    stroke2(C.barrier, 3, 6);

    // the play boundary — one smooth white barrier
    tracePath(TABLE.bound, false);
    stroke2(C.barrier, 5, 9);

    // mid-field pegs
    for (const p of TABLE.posts) puck(p.x, p.y, p.r, C.post, C.postTop);

    // bumpers
    for (let i = 0; i < TABLE.bumpers.length; i++) {
      const bp = TABLE.bumpers[i];
      bumper(bp.x, bp.y, bp.r, bp.kind, state.lit[i]);
    }

    // flippers — tapered yellow bats
    for (const which of ['L', 'R']) {
      const s = flipperSeg(which);
      bat(s.ax, s.ay, s.bx, s.by, 9.5, 4.5, C.edge);
      bat(s.ax, s.ay, s.bx, s.by, 7.5, 3, C.flip);
      bat(s.ax, s.ay, s.ax + (s.bx - s.ax) * 0.6, s.ay + (s.by - s.ay) * 0.6, 3.5, 2, C.flipTop);
    }

    // plunger — a metal rod that pulls DOWN, away from the ball, as it
    // charges; small ticks along the shaft read as a spring without a
    // busy zigzag coil. Sits well below the resting ball, so it never
    // visually overlaps it.
    {
      const lx = TABLE.laneX;
      const restY = 503; // touching the ball, uncharged
      const pullY = 513; // fully pulled back
      const anchorY = 522; // fixed base
      const knobY = restY + state.charge * (pullY - restY);
      ctx.beginPath();
      ctx.moveTo(lx, anchorY);
      ctx.lineTo(lx, knobY);
      stroke2(C.metal, 3, 6);
      ctx.strokeStyle = 'rgba(28,34,48,0.45)';
      ctx.lineWidth = 1.5;
      for (let y = knobY + 3; y < anchorY - 1; y += 4) {
        ctx.beginPath();
        ctx.moveTo(lx - 3, y);
        ctx.lineTo(lx + 3, y);
        ctx.stroke();
      }
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(lx - 6, knobY - 4, 12, 8, 3);
      else ctx.rect(lx - 6, knobY - 4, 12, 8);
      ctx.fillStyle = C.metal;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = C.edge;
      ctx.stroke();
    }

    // ball — a spinning Poké Ball
    const b = state.ball;
    if (b) {
      const r = PHYS.ballR;
      ctx.beginPath();
      ctx.arc(b.x + 1.5, b.y + 2, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fill();
      pokeball(b.x, b.y, r, b.spin, accent);
    }

    // catch — the table is frozen; show a scoreboard card over a dim wash
    if (state.catchT > 0) {
      const name = (state.catchMon?.name || 'a Pokémon').toUpperCase();
      ctx.fillStyle = 'rgba(10,12,18,0.55)';
      ctx.fillRect(0, 0, W, H);
      const cy = 292;
      const cw = 244;
      const ch = 92;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(W / 2 - cw / 2, cy - ch / 2, cw, ch, 14);
      else ctx.rect(W / 2 - cw / 2, cy - ch / 2, cw, ch);
      ctx.fillStyle = C.edge;
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#ffd23f';
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 21px system-ui, sans-serif';
      ctx.fillText('Gotcha!', W / 2, cy - 6);
      ctx.fillStyle = '#ffd23f';
      ctx.font = 'bold 15px system-ui, sans-serif';
      ctx.fillText(`${name} was caught!`, W / 2, cy + 18);
      ctx.textAlign = 'left';
    }
  }

  // One simulation frame (no drawing) — split out so a headless harness can
  // drive the physics deterministically without requestAnimationFrame.
  function frame(fdt, now) {
    fdt = Math.min(fdt || 0, 1 / 30);
    // A catch freezes the table — the ball hangs where it was while the
    // celebration plays out, then the game resumes.
    if (state.catchT > 0) {
      state.catchT = Math.max(0, state.catchT - fdt);
      state.charge = state.chargeStart ? Math.min(1, (now - state.chargeStart) / PULL_MS) : 0;
      return;
    }
    const sdt = fdt / PHYS.substeps;
    for (let i = 0; i < PHYS.substeps; i++) step(sdt);
    state.charge = state.chargeStart ? Math.min(1, (now - state.chargeStart) / PULL_MS) : 0;
    cbs.onCharge?.(state.charge);
  }

  function loop(now) {
    if (!state.running) return;
    frame((now - state.last) / 1000, now);
    state.last = now;
    render();
    state.raf = requestAnimationFrame(loop);
  }

  let ro = null;
  function start() {
    if (state.running) return;
    state.running = true;
    state.last = performance.now();
    newGame();
    resize();
    if (typeof ResizeObserver === 'function' && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    }
    // Layout may not be settled on the first frame.
    requestAnimationFrame(resize);
    state.raf = requestAnimationFrame(loop);
  }

  function stop() {
    state.running = false;
    cancelAnimationFrame(state.raf);
    ro?.disconnect();
    ro = null;
  }

  return {
    start,
    stop,
    newGame,
    setFlipper: (which, down) => {
      state.flip[which] = !!down;
    },
    setPlunge: (down) => {
      down = !!down;
      const b = state.ball;
      if (down && !state.plunge && b?.inLane) {
        state.chargeStart = performance.now();
      } else if (!down && state.plunge && state.chargeStart && b?.inLane) {
        const held = (performance.now() - state.chargeStart) / 1000;
        const c = Math.min(1, held / (PULL_MS / 1000)); // 0 = tap, 1 = full hold
        // Straight up the lane fast enough to reach the hood; step() then
        // flies it into the playfield (see "Hood delivery"). Every pull
        // launches; a hard pull carries it to the far bumpers.
        b.vy = -(1040 + 200 * c);
        b.vx = 0;
        b.inLane = false; // commit — don't let a graze in the lane cancel the launch
        b.launching = true;
        b.hooding = true;
        state.launchC = c;
        state.chargeStart = 0;
        state.charge = 0;
      }
      state.plunge = down;
    },
    isOver: () => state.over,
    /** Read-only snapshot for tests/debugging. */
    peek: () => ({
      score: state.score,
      balls: state.balls,
      over: state.over,
      charge: state.charge,
      ball: state.ball && { ...state.ball },
      running: state.running,
    }),
    /** Advance one frame without drawing — headless harness only. */
    _frame: (fdt, now) => frame(fdt, now),
  };
}
